import { useRolodexEntries, useRolodexHistogram } from 'features/rolodex'
import {
  DEFAULT_SEARCH_OPTIONS,
  GlobalSearchOptions,
  SearchResult,
} from 'features/search/types'
import {
  buildIdsQueries,
  extractCountsFromIds,
  rankResults,
} from 'features/search/utils/searchHelpers'
import compact from 'lodash/compact'
import { useMemo, useState } from 'react'
import { RolodexEntryType } from 'types'
import { groupBy } from 'utils/fde/maps'
import { useDebouncedValue } from 'utils/hookHelpers'

export interface UseGlobalSearchResult {
  searchTerm: string
  debouncedSearchTerm: string | undefined
  isDebouncing: boolean
  isLoading: boolean
  counts: Partial<Record<RolodexEntryType, number>>
  allResults: SearchResult[]
  activeFilters: Set<RolodexEntryType>
  toggleFilter: (type: RolodexEntryType) => void
  filteredResults: SearchResult[]
}

export const useGlobalSearch = (
  searchTerm: string,
  siteID?: string,
  orgID?: string,
  options: GlobalSearchOptions = {}
): UseGlobalSearchResult => {
  const mergedOptions = { ...DEFAULT_SEARCH_OPTIONS, ...options }
  const { searchableTypes, expandedLimit, debounceMs, minSearchLength } =
    mergedOptions

  const [activeFilters, setActiveFilters] = useState<Set<RolodexEntryType>>(
    new Set()
  )

  const { value: debouncedTerm, isDebouncing } = useDebouncedValue(
    searchTerm,
    debounceMs
  )

  const shouldSearch = useMemo(() => {
    return (
      debouncedTerm !== undefined && debouncedTerm.length >= minSearchLength
    )
  }, [debouncedTerm, minSearchLength])

  const idsQueries = useMemo(() => {
    if (!shouldSearch || !debouncedTerm) return []
    if (!siteID || !orgID) return []
    return searchableTypes.flatMap((type) =>
      buildIdsQueries({
        searchTerm: debouncedTerm,
        entityType: type,
        siteID,
        orgID,
      })
    )
  }, [shouldSearch, debouncedTerm, searchableTypes, siteID, orgID])

  const { data: histogramDataArrays, isLoading: idsLoading } =
    useRolodexHistogram(idsQueries)

  const idsMap = useMemo(() => {
    if (!histogramDataArrays || histogramDataArrays.length === 0) return {}

    const map: Partial<Record<RolodexEntryType, Set<string>>> = {}

    histogramDataArrays.forEach((histogramDataArray) => {
      histogramDataArray.forEach((histogramData) => {
        const type = histogramData.type as RolodexEntryType
        const id = histogramData.id as string
        const currentSet = map[type] || new Set()
        currentSet.add(id)
        map[type] = currentSet
      })
    })
    return map
  }, [histogramDataArrays])

  const counts = useMemo(() => {
    return extractCountsFromIds(idsMap)
  }, [idsMap])

  const entryQueries = useMemo(() => {
    const queries = compact(
      searchableTypes.map((type) => {
        const ids = idsMap[type]

        if (!ids || ids.size === 0) {
          return undefined
        }

        const limitedIds = Array.from(ids).slice(0, expandedLimit)

        return {
          type,
          ids: limitedIds,
        }
      })
    )
    return queries.length > 0 ? queries : undefined
  }, [searchableTypes, idsMap, expandedLimit])

  const { entries: entryQueryResults, isLoading: entryQueryLoading } =
    useRolodexEntries(entryQueries)

  const isLoading = useMemo(() => {
    return idsLoading || entryQueryLoading
  }, [idsLoading, entryQueryLoading])

  const entriesByType = useMemo(() => {
    return groupBy({
      arr: entryQueryResults,
      key: 'type',
    })
  }, [entryQueryResults])

  const allResults = useMemo<SearchResult[]>(() => {
    if (!shouldSearch || !debouncedTerm) return []

    const results: SearchResult[] = []

    searchableTypes.forEach((type) => {
      const currEntries = entriesByType.get(type) || []
      const rankedResults = rankResults(currEntries, debouncedTerm)

      rankedResults.forEach((result) => {
        results.push({
          entry: result.entry,
          relevanceScore: result.relevanceScore,
        })
      })
    })

    return results.sort((a, b) => b.relevanceScore - a.relevanceScore)
  }, [shouldSearch, debouncedTerm, searchableTypes, entriesByType])

  const filteredResults = useMemo<SearchResult[]>(() => {
    if (activeFilters.size === 0) {
      return allResults
    }

    return allResults.filter((result) =>
      activeFilters.has(result.entry.type as RolodexEntryType)
    )
  }, [allResults, activeFilters])

  const toggleFilter = (type: RolodexEntryType) => {
    setActiveFilters((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(type)) {
        newSet.delete(type)
      } else {
        newSet.add(type)
      }
      return newSet
    })
  }

  return {
    searchTerm,
    debouncedSearchTerm: debouncedTerm,
    isDebouncing,
    isLoading,
    counts,
    allResults,
    activeFilters,
    toggleFilter,
    filteredResults,
  }
}
